// File: frontend/src/App.js

import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'; 
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext'; 

import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import SocialFollowBar from './components/common/SocialFollowBar'; 
import Spinner from './components/common/Spinner'; 
import ProtectedRoute from './routes/ProtectedRoute'; 
import ScrollToTop from './utils/ScrollToTop';
import GoogleAnalytics from './components/common/GoogleAnalytics';

// ✅ IMPORT 1: WebStory Editor (Create/Edit)
import WebStoryEditor from './pages/admin/WebStoryEditor';

// ✅ IMPORT 2: Manage Web Stories (List View) - YE MISSING THA SHAYAD
import ManageWebStories from './pages/admin/ManageWebStories';

// 🔥 PERFORMANCE UPDATE: Direct Import for HomePage (Instant Load)
import HomePage from './pages/public/HomePage'; 

// --- Lazy Imports (Other pages load in background) ---
const ArticlePage = lazy(() => import('./pages/public/ArticlePage'));
const CategoryPage = lazy(() => import('./pages/public/CategoryPage'));
const SearchPage = lazy(() => import('./pages/public/SearchPage'));
const SubscribePage = lazy(() => import('./pages/public/SubscribePage'));

const AboutUsPage = lazy(() => import('./pages/public/AboutUsPage'));
const ContactUsPage = lazy(() => import('./pages/public/ContactUsPage'));
const AdvertisePage = lazy(() => import('./pages/public/AdvertisePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/public/PrivacyPolicyPage'));
const TermsConditionPage = lazy(() => import('./pages/public/TermsConditionPage'));

// Admin Components (Lazy Loaded)
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const ArticleEditor = lazy(() => import('./pages/admin/ArticleEditor'));
const ManageTeam = lazy(() => import('./pages/admin/ManageTeam')); 
const Approvals = lazy(() => import('./pages/admin/Approvals')); 
const Subscribers = lazy(() => import('./pages/admin/Subscribers')); 
const PublishedArticles = lazy(() => import('./pages/admin/PublishedArticles')); 

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <AuthProvider>
          <Router>
            <GoogleAnalytics /> 
            <ScrollToTop />
            
            <div className="App"> 
              <Navbar />
              <SocialFollowBar />
              
              <main>
                <Suspense fallback={
                    <div style={{height:'80vh', display:'flex', justifyContent:'center', alignItems:'center'}}>
                        <Spinner />
                    </div>
                }> 
                  <Routes>
                    {/* --- Public Routes --- */}
                    <Route path="/" element={<HomePage />} />
                    <Route path="/article/:slug" element={<ArticlePage />} />
                    <Route path="/category/:category" element={<CategoryPage />} />
                    <Route path="/category/:category/:subcategory" element={<CategoryPage />} />
                    <Route path="/category/:category/:subcategory/:district" element={<CategoryPage />} />
                    
                    <Route path="/search" element={<SearchPage />} />
                    <Route path="/subscribe" element={<SubscribePage />} />

                    <Route path="/about" element={<AboutUsPage />} />
                    <Route path="/contact" element={<ContactUsPage />} />
                    <Route path="/advertise" element={<AdvertisePage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="/terms-condition" element={<TermsConditionPage />} />

                    <Route path="/admin/login" element={<AdminLogin />} />
                    
                    {/* --- ADMIN ONLY ROUTES --- */}
                    <Route element={<ProtectedRoute adminOnly={true} />}>
                        <Route path="/admin" element={<AdminDashboard />} />
                        <Route path="/admin/team" element={<ManageTeam />} />
                        <Route path="/admin/approvals" element={<Approvals />} /> 
                        <Route path="/admin/subscribers" element={<Subscribers />} />
                        <Route path="/admin/articles" element={<PublishedArticles />} /> 
                        
                        {/* ✅ NEW: Web Stories Routes */}
                        {/* 1. List Page (Manage) */}
                        <Route path="/admin/web-stories" element={<ManageWebStories />} />
                        
                        {/* 2. Create Page */}
                        <Route path="/admin/web-stories/create" element={<WebStoryEditor />} />
                        
                        {/* 3. Edit Page */}
                        <Route path="/admin/web-stories/edit/:id" element={<WebStoryEditor />} />
                    </Route>

                    {/* --- SHARED ROUTES --- */}
                    <Route element={<ProtectedRoute adminOnly={false} />}>
                        <Route path="/admin/editor" element={<ArticleEditor />} />
                        <Route path="/admin/editor/:id" element={<ArticleEditor />} />
                    </Route>

                  </Routes>
                </Suspense>
              </main>
              
              <Footer />
            </div>
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;